import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest, queryStringify } from '@musinsa/packages-http';
import urlJoin from 'url-join';
import z from 'zod';
import { privateHttpClient } from '../httpClient';
import { createApiResponseSchema } from '../utils';
import { campaignsApiPath } from './apiPath';

const GetSeedingProductOptionsRequestSchema = z
  .object({
    campaignId: z.number().int(),
    /** 옵션을 조회할 상품 번호들 (최대 50). */
    goodsNos: z.array(z.number().int()).min(1).max(50),
  })
  .describe('GetSeedingProductOptionsRequest');

export type GetSeedingProductOptionsRequest = z.infer<typeof GetSeedingProductOptionsRequestSchema>;

const OptionValueSchema = z
  .object({
    valueId: z.number().int(),
    name: z.string(),
    color: z.string().nullable().optional(),
    imageUrl: z.string().nullable().optional(),
  })
  .describe('SeedingOptionValue');

const OptionGroupSchema = z
  .object({
    optionName: z.string(),
    values: z.array(OptionValueSchema),
  })
  .describe('SeedingOptionGroup');

const SkuSchema = z
  .object({
    skuId: z.number().int(),
    valueIds: z.array(z.number().int()),
    outOfStock: z.boolean(),
    extraPrice: z.number().int(),
  })
  .describe('SeedingSku');

const ExtraOptionGroupValueSchema = z
  .object({
    valueId: z.number().int(),
    name: z.string(),
    // BE 는 추가옵션 값 가격을 `price` 로 내려준다 (SKU 의 `extraPrice` 와 필드명이 다름).
    price: z.number().int(),
    outOfStock: z.boolean(),
  })
  .describe('SeedingExtraOptionGroupValue');

const ExtraOptionGroupSchema = z
  .object({
    extraOptionId: z.number().int(),
    name: z.string(),
    required: z.boolean(),
    sequence: z.number().int(),
    values: z.array(ExtraOptionGroupValueSchema),
  })
  .describe('SeedingExtraOptionGroup');

const ProductOptionsItemSchema = z
  .object({
    goodsNo: z.number().int(),
    options: z.array(OptionGroupSchema),
    skus: z.array(SkuSchema),
    extraOptions: z.array(ExtraOptionGroupSchema),
  })
  .describe('SeedingProductOptionsItem');

export type SeedingProductOptionsItem = z.infer<typeof ProductOptionsItemSchema>;

const GetSeedingProductOptionsResponseDataSchema = z
  .object({
    items: z.array(ProductOptionsItemSchema),
  })
  .describe('GetSeedingProductOptionsResponseData');

const GetSeedingProductOptionsResponseSchema = createApiResponseSchema(
  GetSeedingProductOptionsResponseDataSchema,
);

export type GetSeedingProductOptionsResponse = z.infer<
  typeof GetSeedingProductOptionsResponseSchema
>;

export const getSeedingProductOptions = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetSeedingProductOptionsRequestSchema),
    async ({
      campaignId,
      goodsNos,
    }: GetSeedingProductOptionsRequest): Promise<GetSeedingProductOptionsResponse> => {
      return executeStandardRequest(async () =>
        privateHttpClient.get<GetSeedingProductOptionsResponse>(
          urlJoin(campaignsApiPath.getSeedingProductOptions(campaignId)),
          // 2번째 인자 = query params, 3번째 인자 = config. config 를 params 에 합치면
          // params/paramsSerializer 가 그대로 직렬화되어 URL 로 새어나간다.
          { goodsNos },
          {
            // 배열 query 직렬화 — Spring @RequestParam List 바인딩을 위해 comma(`goodsNos=1,2`).
            // axios 기본(`goodsNos[]=1&goodsNos[]=2`)은 바인딩 실패. getReverseProposalGoods 동일 패턴.
            paramsSerializer: (params) => queryStringify(params, { arrayFormat: 'comma' }),
          },
        ),
      );
    },
    validateApiResponse(GetSeedingProductOptionsResponseSchema),
  ),
);
